import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAppSelector } from "@store/hooks";
import { changePasswordSchema } from "@shared/utils/yupSchema";
import { supabase, updatePassword } from "@services/supabaseAPI";
import { useNotification } from "@shared/contexts/NotificationContext";
import { requireStepUp } from "@shared/utils/stepUpManager";
import DeleteAccountModal from "@/admin-panel/DeleteAccountModal";
import MfaPanel from "./MfaPanel";
import PasswordStrengthMeter from "@shared/components/PasswordStrengthMeter";

interface PasswordForm {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

const EMPTY_FORM: PasswordForm = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

export const SecurityPanel = () => {
  const { userInfo } = useAppSelector((state) => state.auth);
  const { showSuccess, showError } = useNotification();
  const [deleteOpen, setDeleteOpen] = useState(false);
  // editing=false → "Change password" button only. editing=true →
  // the new-password form is visible. Flipping back to false after
  // submit doubles as the success indicator: the form disappears.
  // Avoids the prior pattern's reliance on react-hook-form reset()
  // landing visibly, which could be brittle around the surrounding
  // re-renders triggered by Supabase's USER_UPDATED event.
  const [editing, setEditing] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // OAuth-only accounts (e.g. "Continue with Google") have no password
  // identity, so the change-password form would only ever fail for
  // them — Supabase's secure-password-change check demands a current
  // password they don't have. Defaults to true (the historical
  // behavior) until identities load; a fetch failure leaves the form
  // available rather than hiding a real capability.
  const [oauthProviders, setOauthProviders] = useState<string[]>([]);
  const [hasPasswordIdentity, setHasPasswordIdentity] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      const identities = data.user?.identities;
      if (cancelled || !identities || identities.length === 0) return;
      setHasPasswordIdentity(identities.some((i) => i.provider === "email"));
      setOauthProviders(
        identities
          .map((i) => i.provider)
          .filter((p): p is string => !!p && p !== "email")
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setError,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<PasswordForm>({
    resolver: yupResolver(changePasswordSchema),
    defaultValues: EMPTY_FORM,
  });
  // Subscribe to the new-password field so the strength meter updates
  // as the user types. Same pattern as SignUpForm — small form, no
  // expensive children, watch()'s per-keystroke re-render is fine.
  const newPasswordValue = watch("newPassword") ?? "";
  // Feed the user's own identifying info to zxcvbn so it treats their
  // name / email / username as guessable when present in the password.
  const strengthUserInputs = [
    userInfo?.full_name,
    userInfo?.email,
    userInfo?.username,
  ];

  // Triggered by the "Change password" button. Verify identity
  // BEFORE showing the form so the auth-then-change ordering matches
  // user expectations. requireStepUp returns true immediately for
  // users without MFA enrolled; for MFA users it shows the step-up
  // dialog and resolves once they verify (taking the session to
  // AAL2). The form is gated on the result so users can't reach it
  // without proving identity.
  const beginEdit = async () => {
    if (!(await requireStepUp())) return;
    setEditing(true);
  };

  const onSubmit = async (data: PasswordForm) => {
    setSubmitError(null);

    // Supabase's "Secure password change" check is satisfied by
    // passing current_password directly to updateUser — no separate
    // signInWithPassword needed (which would drop AAL2 for MFA
    // users). AAL2 is already secured by the requireStepUp call in
    // beginEdit.

    // Race the SDK's updateUser promise against the USER_UPDATED event.
    // In versions where the SDK promise can hang post-password-rotation,
    // the event still fires from inside the response-handling path and
    // gives us a reliable signal that the change landed server-side.
    // We deliberately do NOT include a wall-clock timeout: a false
    // success would let the form close while the underlying PUT was
    // still in flight, leaving Supabase JS's session lock held and
    // stranding the next sign-in's bootstrap.
    let unsubscribe = () => {};
    const userUpdatedFromEvent = new Promise<{
      success: true;
      error: string;
    }>((resolve) => {
      const { data: sub } = supabase.auth.onAuthStateChange((event) => {
        if (event === "USER_UPDATED") {
          resolve({ success: true, error: "" });
        }
      });
      unsubscribe = () => sub.subscription.unsubscribe();
    });
    try {
      const result = await Promise.race([
        updatePassword(data.newPassword, data.currentPassword),
        userUpdatedFromEvent,
      ]);
      if (!result.success) {
        const msg = result.error || "Could not update password";
        // When Supabase rejects on the current-password check, pin
        // the error to that field so the user can correct it in
        // place. Otherwise surface as a form-level error.
        if (/current\s*password/i.test(msg)) {
          setError("currentPassword", {
            type: "manual",
            message: "Current password is incorrect.",
          });
          return;
        }
        setSubmitError(msg);
        showError(msg);
        return;
      }
      showSuccess("Password changed");
      reset(EMPTY_FORM);
      setEditing(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setSubmitError(msg);
      showError(msg);
    } finally {
      // Always release the listener — when the SDK promise wins the
      // race, the USER_UPDATED handler is never triggered and the
      // subscription would otherwise leak.
      unsubscribe();
    }
  };

  const cancelEdit = () => {
    reset(EMPTY_FORM);
    setSubmitError(null);
    setEditing(false);
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Security
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {hasPasswordIdentity
            ? "Change your password or delete your account."
            : "Manage two-factor authentication or delete your account."}
        </Typography>

        {!hasPasswordIdentity ? (
          <Typography variant="body2" color="text.secondary">
            You sign in with{" "}
            {oauthProviders
              .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
              .join(", ") || "an external provider"}
            , so this account has no password to change.
          </Typography>
        ) : editing ? (
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              {...register("currentPassword")}
              autoFocus
              autoComplete="current-password"
              label="Current password"
              type="password"
              size="small"
              fullWidth
              disabled={isSubmitting}
              error={!!errors.currentPassword}
              helperText={errors.currentPassword?.message ?? " "}
              sx={{ mb: 2 }}
            />
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              sx={{ mb: 2 }}
            >
              <TextField
                {...register("newPassword")}
                autoComplete="new-password"
                label="New password"
                type="password"
                size="small"
                fullWidth
                disabled={isSubmitting}
                error={!!errors.newPassword}
                helperText={errors.newPassword?.message ?? " "}
              />
              <TextField
                {...register("confirmPassword")}
                autoComplete="new-password"
                label="Confirm new password"
                type="password"
                size="small"
                fullWidth
                disabled={isSubmitting}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword?.message ?? " "}
              />
            </Stack>
            <Box sx={{ mb: 2 }}>
              <PasswordStrengthMeter
                password={newPasswordValue}
                userInputs={strengthUserInputs}
              />
            </Box>
            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
            <Stack direction="row" spacing={1}>
              <Button
                type="submit"
                variant="outlined"
                color="inherit"
                disabled={isSubmitting || !isDirty}
              >
                {isSubmitting ? "Saving…" : "Save new password"}
              </Button>
              <Button
                type="button"
                variant="outlined"
                color="cancel"
                disabled={isSubmitting}
                onClick={cancelEdit}
              >
                Cancel
              </Button>
            </Stack>
          </form>
        ) : (
          <Button variant="outlined" color="inherit" onClick={beginEdit}>
            Change password
          </Button>
        )}

        <MfaPanel />

        <Box
          sx={{
            mt: 4,
            pt: 3,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="subtitle1">Delete account</Typography>
            <Typography variant="body2" color="text.secondary">
              Permanently remove your account and all associated data.
            </Typography>
          </Box>
          <Button
            color="error"
            variant="outlined"
            onClick={() => setDeleteOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Delete account
          </Button>
        </Box>
      </Paper>
      <DeleteAccountModal
        modalVisible={deleteOpen}
        setModalVisible={setDeleteOpen}
        user={userInfo?.email ?? ""}
      />
    </>
  );
};

export default SecurityPanel;
